import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChatConversationSummary, ChatMessageAttachment, ChatUploadSummary } from '../../interfaces/ChatInterface';
import { listChatConversations } from '../../services/chatService';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import useMainContentClass from '../hooks/useMainContentClass';
import Loader from '../reusable/Loader';
import PageHeader from '../reusable/PageHeader';
import ChatComposer from './ChatComposer';
import ChatConversationList from './ChatConversationList';
import ChatConversationView from './ChatConversationView';
import ChatDataPanel from './ChatDataPanel';
import ChatInstructionsPanel from './ChatInstructionsPanel';
import ChatUploadsPanel from './ChatUploadsPanel';
import Panel from '../reusable/Panel';
import { newConversationScope } from './chatUtils';
import './Chat.css';

const SCOPE_BASE = 'main';

type ChatView =
  | { kind: 'landing' }
  | { kind: 'list' }
  | { kind: 'compose' }
  | { kind: 'conversation'; scope: string; initialPrompt?: string; initialAttachments?: ChatMessageAttachment[] };

const ChatPage = () => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const [conversations, setConversations] = useState<ChatConversationSummary[]>([]);
  const [view, setView] = useState<ChatView | null>(null);
  const [composeScope, setComposeScope] = useState<string>(() => newConversationScope(SCOPE_BASE));

  const location = useLocation();
  const lastNavKeyRef = useRef(location.key);

  const eoaId = userDetails?.eoaId;
  const accessToken = userDetails?.access_token;

  const fetchConversations = useCallback(async (): Promise<ChatConversationSummary[]> => {
    if (!eoaId || !accessToken) return [];
    try {
      const items = await listChatConversations(eoaId, accessToken, SCOPE_BASE);
      setConversations(items);
      return items;
    } catch (error) {
      handleOpenErrorModal('Failed to load conversations', error as Error);
      return [];
    }
  }, [eoaId, accessToken, handleOpenErrorModal]);

  useEffect(() => {
    if (!eoaId || !accessToken) return;
    void (async () => {
      const items = await fetchConversations();
      setView((current) => current ?? (items.length > 0 ? { kind: 'list' } : { kind: 'landing' }));
    })();
  }, [eoaId, accessToken, fetchConversations]);

  useMainContentClass(view?.kind === 'conversation' || view?.kind === 'compose' ? 'chat-white-background' : '');

  const startConversationFromPrompt = (prompt: string, attachments?: ChatUploadSummary[]) => {
    const initialAttachments = attachments?.map(({ id, original_file_name, size_bytes }) => ({
      id,
      original_file_name,
      size_bytes,
    }));
    setView({ kind: 'conversation', scope: composeScope, initialPrompt: prompt, initialAttachments });
    setComposeScope(newConversationScope(SCOPE_BASE));
  };

  const startNewChat = () => {
    setComposeScope(newConversationScope(SCOPE_BASE));
    setView({ kind: 'compose' });
  };

  const openConversation = (scope: string) => {
    setView({ kind: 'conversation', scope });
  };

  const leaveConversation = useCallback(async () => {
    const items = await fetchConversations();
    setView(items.length > 0 ? { kind: 'list' } : { kind: 'landing' });
  }, [fetchConversations]);

  // Re-selecting "District report tool" in the nav re-navigates to this route, generating a fresh
  // location key; treat that as "return to the overview" rather than staying on the current view.
  useEffect(() => {
    if (location.key === lastNavKeyRef.current) return;
    lastNavKeyRef.current = location.key;
    void leaveConversation();
  }, [location.key, leaveConversation]);

  if (!userDetails || view === null) return <Loader />;

  return (
    <>
      <PageHeader title="District Report Tool" />
      <div className="main-content chat-page">
        {view.kind === 'landing' && eoaId && accessToken && (
          <ChatComposer
            eoaId={eoaId}
            accessToken={accessToken}
            scope={composeScope}
            userName={userDetails.name}
            onSubmit={startConversationFromPrompt}
          />
        )}

        {view.kind === 'list' && (
          <ChatConversationList conversations={conversations} onNewChat={startNewChat} onSelect={openConversation} />
        )}

        {view.kind === 'compose' && eoaId && accessToken && (
          <ChatComposer
            eoaId={eoaId}
            accessToken={accessToken}
            scope={composeScope}
            userName={userDetails.name}
            onSubmit={startConversationFromPrompt}
            onBack={() => setView({ kind: 'list' })}
          />
        )}

        {view.kind === 'conversation' && eoaId && accessToken && (
          <ChatConversationView
            eoaId={eoaId}
            accessToken={accessToken}
            scope={view.scope}
            title={conversations.find((conversation) => conversation.scope === view.scope)?.title}
            initialPrompt={view.initialPrompt}
            initialAttachments={view.initialAttachments}
            onBack={() => void leaveConversation()}
            onNewChat={startNewChat}
            onConversationChanged={() => void fetchConversations()}
          />
        )}

        {(view.kind === 'landing' || view.kind === 'list') && eoaId && accessToken && (
          <>
            <Panel>
              <ChatDataPanel />
            </Panel>
            <Panel className="chat-uploads-instructions-panel">
              <ChatUploadsPanel eoaId={eoaId} accessToken={accessToken} />
              <ChatInstructionsPanel eoaId={eoaId} accessToken={accessToken} />
            </Panel>
          </>
        )}
      </div>
    </>
  );
};

export default ChatPage;
